import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  LineChart, Line, CartesianGrid, Legend,
} from "recharts";
import { api, formatApiError } from "../lib/apiClient";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";

const LEVEL_LABELS = {
  n0: "Prevalidación N0",
  n1: "Diagnóstico N1",
  n2: "Kit Documental N2",
  diag_light: "Diag. ligero",
  email: "Email IA",
};

const LEVEL_COLORS = {
  n0: "#9ca3af",
  n1: "#a16e3a",
  n2: "#7a2e2e",
  email: "#5e7ac8",
  other: "#cbd5e1",
};

function formatUSD(n) {
  if (n == null || isNaN(n)) return "—";
  if (Math.abs(n) >= 1) return `$${Number(n).toFixed(2)}`;
  return `$${Number(n).toFixed(4)}`;
}

function formatCOP(n) {
  if (n == null) return "—";
  return new Intl.NumberFormat("es-CO", {
    style: "currency", currency: "COP", maximumFractionDigits: 0,
  }).format(n);
}

function formatMs(n) {
  if (n == null) return "—";
  if (n >= 1000) return `${(n / 1000).toFixed(1)}s`;
  return `${Math.round(n)}ms`;
}

function isoDaysAgo(days) {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - days);
  return d.toISOString().slice(0, 10) + "T00:00:00.000Z";
}

// Presets temporales rápidos.
const RANGE_PRESETS = [
  { id: "30d", label: "30 días", days: 30, period: "day" },
  { id: "90d", label: "90 días", days: 90, period: "week" },
  { id: "12m", label: "12 meses", days: 365, period: "month" },
  { id: "all", label: "Todo", days: null, period: "month" },
];

export default function AdminAIStatsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rangePreset, setRangePreset] = useState("90d");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [period, setPeriod] = useState("week");
  const [filterLevel, setFilterLevel] = useState("");
  const [filterModel, setFilterModel] = useState("");

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      let p = period;
      if (rangePreset !== "custom") {
        const preset = RANGE_PRESETS.find((r) => r.id === rangePreset);
        if (preset?.days) params.append("date_from", isoDaysAgo(preset.days));
        p = preset?.period || period;
      } else {
        if (customFrom) params.append("date_from", customFrom + "T00:00:00.000Z");
        if (customTo) params.append("date_to", customTo + "T23:59:59.999Z");
      }
      params.append("period", p);
      if (filterLevel) params.append("level", filterLevel);
      if (filterModel) params.append("model", filterModel);
      const { data: res } = await api.get(`/admin/ai-runs/cost-stats?${params}`);
      setData(res);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Error al cargar estadísticas");
    } finally {
      setLoading(false);
    }
  }, [rangePreset, customFrom, customTo, period, filterLevel, filterModel]);

  useEffect(() => { loadData(); }, [loadData]);

  const exportCSV = () => {
    if (!data?.timeseries) return;
    const rows = [["Periodo", "Runs", "Costo USD", "Tokens in", "Tokens out", "Errores"]];
    data.timeseries.forEach((t) => {
      rows.push([t.bucket, t.runs, t.cost_usd, t.tokens_in, t.tokens_out, t.errors]);
    });
    const csv = rows.map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ai-stats-${rangePreset}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const kpis = data?.kpis;
  const usdRate = data?.usd_rate_cop || 3700;

  return (
    <div className="container-prose py-10" data-testid="admin-ai-stats-page">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <Link to="/admin" className="text-sm text-gray-500 hover:text-primary">← Admin</Link>
          <h1 className="font-serif text-3xl text-gray-900 mt-2">Estadísticas de IA</h1>
          <p className="text-sm text-gray-600 mt-1">
            Costos, latencia, consumo de tokens y márgenes proyectados por nivel.
          </p>
        </div>
        <div className="flex gap-2">
          <Link to="/admin/ai-runs">
            <Button variant="outline" className="rounded-sm h-10" data-testid="goto-ai-runs-btn">Ver runs ↗</Button>
          </Link>
          <Button onClick={exportCSV} disabled={!data} className="rounded-sm h-10" data-testid="export-csv-btn">Export CSV</Button>
        </div>
      </div>

      {/* FILTROS */}
      <div className="border border-gray-200 bg-white p-5 mb-6" data-testid="filters-panel">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="text-[11px] uppercase tracking-widest text-gray-500 block mb-1">Período</label>
            <div className="flex gap-1">
              {RANGE_PRESETS.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => { setRangePreset(r.id); setPeriod(r.period); }}
                  className={`px-3 h-10 text-xs border ${rangePreset === r.id ? 'bg-primary text-white border-primary' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
                  data-testid={`preset-${r.id}`}
                >{r.label}</button>
              ))}
              <button
                type="button"
                onClick={() => setRangePreset("custom")}
                className={`px-3 h-10 text-xs border ${rangePreset === "custom" ? 'bg-primary text-white border-primary' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
                data-testid="preset-custom"
              >Custom</button>
            </div>
          </div>
          {rangePreset === "custom" && (
            <>
              <div>
                <label className="text-[11px] uppercase tracking-widest text-gray-500 block mb-1">Desde</label>
                <Input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} className="h-10 w-40" data-testid="custom-from" />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-widest text-gray-500 block mb-1">Hasta</label>
                <Input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} className="h-10 w-40" data-testid="custom-to" />
              </div>
            </>
          )}
          <div>
            <label className="text-[11px] uppercase tracking-widest text-gray-500 block mb-1">Granularidad</label>
            <select className="h-10 px-2 text-sm border border-gray-300" value={period} onChange={(e) => setPeriod(e.target.value)} data-testid="granularity">
              <option value="day">Día</option>
              <option value="week">Semana</option>
              <option value="month">Mes</option>
            </select>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-widest text-gray-500 block mb-1">Nivel</label>
            <select className="h-10 px-2 text-sm border border-gray-300" value={filterLevel} onChange={(e) => setFilterLevel(e.target.value)} data-testid="filter-level">
              <option value="">Todos</option>
              <option value="n0">N0</option>
              <option value="n1">N1</option>
              <option value="n2">N2</option>
              <option value="email">Email</option>
            </select>
          </div>
          <div className="flex-1 min-w-[180px]">
            <label className="text-[11px] uppercase tracking-widest text-gray-500 block mb-1">Modelo</label>
            <Input value={filterModel} onChange={(e) => setFilterModel(e.target.value)} placeholder="ej: claude-sonnet-4.5" className="h-10" data-testid="filter-model" />
          </div>
          <Button onClick={loadData} disabled={loading} className="rounded-sm h-10" data-testid="apply-btn">
            {loading ? "Cargando…" : "Aplicar"}
          </Button>
        </div>
      </div>

      {/* KPIs */}
      {kpis && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-6" data-testid="kpis">
          <Kpi label="Costo total" value={formatUSD(kpis.total_cost_usd)} sub={formatCOP(kpis.total_cost_usd * usdRate)} testid="kpi-total-cost" />
          <Kpi label="Runs" value={kpis.total_runs.toLocaleString()} testid="kpi-total-runs" />
          <Kpi label="Costo / caso" value={formatUSD(kpis.avg_cost_per_case_usd)} sub={`${kpis.unique_cases} casos`} testid="kpi-cost-per-case" />
          <Kpi label="Costo / run" value={formatUSD(kpis.avg_cost_per_run_usd)} testid="kpi-cost-per-run" />
          <Kpi label="Lat. promedio" value={formatMs(kpis.avg_latency_ms)} testid="kpi-avg-latency" />
          <Kpi label="Error rate" value={`${kpis.error_rate_pct}%`} sub={`${kpis.errors} errores`} testid="kpi-error-rate" tone={kpis.error_rate_pct > 5 ? "warn" : "ok"} />
        </div>
      )}

      {/* TIME SERIES CHARTS */}
      {data?.timeseries && data.timeseries.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
          <ChartCard title="Costo USD por período" testid="chart-cost">
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={data.timeseries}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="bucket" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `$${v}`} />
                <Tooltip formatter={(v) => formatUSD(v)} />
                <Bar dataKey="cost_usd" fill="#7a2e2e" name="Costo USD" />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
          <ChartCard title="Runs por período" testid="chart-runs">
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={data.timeseries}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="bucket" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="runs" stroke="#a16e3a" name="Runs" strokeWidth={2} />
                <Line type="monotone" dataKey="errors" stroke="#dc2626" name="Errores" strokeWidth={1} />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>
      )}

      {/* BREAKDOWN POR NIVEL */}
      <div className="border border-gray-200 bg-white mb-6" data-testid="by-level-panel">
        <div className="p-4 border-b border-gray-200">
          <h2 className="font-serif text-lg">Breakdown por nivel + márgenes proyectados</h2>
          <p className="text-xs text-gray-500 mt-1">Tasa USD/COP aplicada: {formatCOP(usdRate)}</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[11px] uppercase tracking-widest text-gray-500 bg-gray-50 border-b border-gray-200">
                <th className="px-3 py-2 text-left">Nivel</th>
                <th className="px-3 py-2 text-right">Runs</th>
                <th className="px-3 py-2 text-right">Casos únicos</th>
                <th className="px-3 py-2 text-right">Costo total</th>
                <th className="px-3 py-2 text-right">Costo / caso</th>
                <th className="px-3 py-2 text-right">Costo / artefacto</th>
                <th className="px-3 py-2 text-right">Precio venta</th>
                <th className="px-3 py-2 text-right">Margen / artefacto</th>
                <th className="px-3 py-2 text-right">Margen %</th>
                <th className="px-3 py-2 text-right">Lat. avg</th>
                <th className="px-3 py-2 text-right">Err</th>
              </tr>
            </thead>
            <tbody>
              {(data?.by_level || []).map((r) => (
                <tr key={r.level} data-testid={`by-level-row-${r.level}`} className="border-b border-gray-100">
                  <td className="px-3 py-2">
                    <span className="inline-block w-2 h-2 rounded-full mr-2" style={{ background: LEVEL_COLORS[r.level] || LEVEL_COLORS.other }} />
                    {LEVEL_LABELS[r.level] || r.level}
                  </td>
                  <td className="px-3 py-2 text-right">{r.runs}</td>
                  <td className="px-3 py-2 text-right">{r.unique_cases}</td>
                  <td className="px-3 py-2 text-right font-mono">{formatUSD(r.total_cost_usd)}</td>
                  <td className="px-3 py-2 text-right font-mono">{formatUSD(r.avg_cost_per_case_usd)}</td>
                  <td className="px-3 py-2 text-right font-mono">{formatUSD(r.avg_cost_per_artifact_usd)}</td>
                  <td className="px-3 py-2 text-right font-mono text-xs">{r.price_cop ? formatCOP(r.price_cop) : "—"}</td>
                  <td className="px-3 py-2 text-right font-mono">
                    {r.margin_per_artifact_usd == null ? "—" : (
                      <span className={r.margin_per_artifact_usd >= 0 ? "text-emerald-700" : "text-rose-600"}>
                        {formatUSD(r.margin_per_artifact_usd)}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right font-mono">
                    {r.margin_pct == null ? "—" : (
                      <span className={`font-semibold ${r.margin_pct >= 95 ? 'text-emerald-700' : r.margin_pct >= 80 ? 'text-amber-600' : 'text-rose-600'}`}>
                        {r.margin_pct}%
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right text-xs">{formatMs(r.avg_latency_ms)}</td>
                  <td className="px-3 py-2 text-right text-xs">{r.errors}</td>
                </tr>
              ))}
              {(!data?.by_level || data.by_level.length === 0) && (
                <tr><td colSpan={11} className="text-center text-gray-500 py-6">Sin datos en el rango</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* BREAKDOWN POR MODELO */}
      <div className="border border-gray-200 bg-white mb-6" data-testid="by-model-panel">
        <div className="p-4 border-b border-gray-200">
          <h2 className="font-serif text-lg">Breakdown por modelo</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[11px] uppercase tracking-widest text-gray-500 bg-gray-50 border-b border-gray-200">
                <th className="px-3 py-2 text-left">Modelo</th>
                <th className="px-3 py-2 text-right">Runs</th>
                <th className="px-3 py-2 text-right">Costo total</th>
                <th className="px-3 py-2 text-right">Costo / run</th>
                <th className="px-3 py-2 text-right">Tokens in</th>
                <th className="px-3 py-2 text-right">Tokens out</th>
                <th className="px-3 py-2 text-right">Lat. avg</th>
                <th className="px-3 py-2 text-right">Errores</th>
              </tr>
            </thead>
            <tbody>
              {(data?.by_model || []).map((r) => (
                <tr key={r.model} data-testid={`by-model-row-${r.model}`} className="border-b border-gray-100">
                  <td className="px-3 py-2 font-mono text-xs">{r.model}</td>
                  <td className="px-3 py-2 text-right">{r.runs}</td>
                  <td className="px-3 py-2 text-right font-mono">{formatUSD(r.total_cost_usd)}</td>
                  <td className="px-3 py-2 text-right font-mono">{formatUSD(r.avg_cost_per_run_usd)}</td>
                  <td className="px-3 py-2 text-right text-xs">{r.tokens_in.toLocaleString()}</td>
                  <td className="px-3 py-2 text-right text-xs">{r.tokens_out.toLocaleString()}</td>
                  <td className="px-3 py-2 text-right text-xs">{formatMs(r.avg_latency_ms)}</td>
                  <td className="px-3 py-2 text-right text-xs">{r.errors > 0 ? <span className="text-rose-600 font-semibold">{r.errors}</span> : 0}</td>
                </tr>
              ))}
              {(!data?.by_model || data.by_model.length === 0) && (
                <tr><td colSpan={8} className="text-center text-gray-500 py-6">Sin datos</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* TOP RUNS */}
      <div className="border border-gray-200 bg-white" data-testid="top-runs-panel">
        <div className="p-4 border-b border-gray-200">
          <h2 className="font-serif text-lg">Top 10 runs más caros</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[11px] uppercase tracking-widest text-gray-500 bg-gray-50 border-b border-gray-200">
                <th className="px-3 py-2 text-left">Fecha</th>
                <th className="px-3 py-2 text-left">Nivel</th>
                <th className="px-3 py-2 text-left">Modelo</th>
                <th className="px-3 py-2 text-left">Caso</th>
                <th className="px-3 py-2 text-right">Costo</th>
                <th className="px-3 py-2 text-right">Tokens</th>
                <th className="px-3 py-2 text-right">Lat</th>
                <th className="px-3 py-2 text-center">Estado</th>
                <th className="px-3 py-2 text-right">Acción</th>
              </tr>
            </thead>
            <tbody>
              {(data?.top_runs || []).map((r) => (
                <tr key={r.id} data-testid={`top-run-${r.id}`} className="border-b border-gray-100">
                  <td className="px-3 py-2 text-xs whitespace-nowrap">{r.created_at?.slice(0, 16).replace("T", " ")}</td>
                  <td className="px-3 py-2 text-xs">{LEVEL_LABELS[r.level] || r.level}</td>
                  <td className="px-3 py-2 font-mono text-xs">{r.model_used || "—"}</td>
                  <td className="px-3 py-2 font-mono text-xs">
                    {r.case_id ? <Link to={`/admin/cases/${r.case_id}`} className="text-primary hover:underline">{r.case_id.slice(0, 8)}…</Link> : "—"}
                  </td>
                  <td className="px-3 py-2 text-right font-mono">{formatUSD(r.cost_usd)}</td>
                  <td className="px-3 py-2 text-right text-xs">{r.tokens_in != null ? `${r.tokens_in}/${r.tokens_out}` : "—"}</td>
                  <td className="px-3 py-2 text-right text-xs">{formatMs(r.latency_ms)}</td>
                  <td className="px-3 py-2 text-center text-xs">
                    {r.error ? <span className="text-rose-600">error</span> : <span className="text-emerald-700">ok</span>}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Link to={`/admin/ai-runs/${r.id}`} className="text-xs text-primary hover:underline" data-testid={`view-top-run-${r.id}`}>Ver →</Link>
                  </td>
                </tr>
              ))}
              {(!data?.top_runs || data.top_runs.length === 0) && (
                <tr><td colSpan={9} className="text-center text-gray-500 py-6">Sin runs registrados</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value, sub, testid, tone }) {
  const toneClass = tone === "warn" ? "border-amber-300 bg-amber-50" : tone === "danger" ? "border-rose-300 bg-rose-50" : "border-gray-200 bg-white";
  return (
    <div className={`border ${toneClass} p-3`} data-testid={testid}>
      <p className="text-[10px] uppercase tracking-widest text-gray-500">{label}</p>
      <p className="text-xl font-serif text-gray-900 mt-1">{value}</p>
      {sub && <p className="text-[10px] text-gray-500 mt-0.5">{sub}</p>}
    </div>
  );
}

function ChartCard({ title, children, testid }) {
  return (
    <div className="border border-gray-200 bg-white" data-testid={testid}>
      <div className="px-4 py-3 border-b border-gray-200">
        <h3 className="font-serif text-base">{title}</h3>
      </div>
      <div className="p-3">{children}</div>
    </div>
  );
}
